import React, { useState } from 'react';
import { passkeyRegisterOptions, passkeyRegisterVerify } from '../services/api';
import { createPasskey, passkeysSupported, passkeyErrorMessage, guessDeviceName } from '../utils/webauthn';
import './TwoFactor.css';

// Passkey enrolment (administrators), shared by the compulsory setup page and
// My account → Passkeys. The server's step-up applies automatically: the
// first passkey asks for a fresh authenticator code, any further one asks for
// an existing passkey (the global prompts handle both).
export default function PasskeyEnrol({ onDone, onCancel, cancelLabel = 'Cancel' }) {
  const [name, setName] = useState(guessDeviceName());
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const supported = passkeysSupported();

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const options = await passkeyRegisterOptions();
      const answer = await createPasskey(options);
      const r = await passkeyRegisterVerify(name.trim() || 'Passkey', answer);
      if (onDone) onDone(r);
    } catch (err) {
      setError(passkeyErrorMessage(err, 'create'));
      setBusy(false);
    }
  };

  return (
    <form className="tf-enrol" onSubmit={create}>
      <ol className="tf-steps">
        <li>Give this passkey a name so you recognise it later (the device it lives on).</li>
        <li>Press <strong>Create passkey</strong> and follow your device&apos;s prompt — fingerprint, face, PIN, a phone via QR code, or a security key.</li>
      </ol>
      <input
        className="form-control"
        type="text"
        maxLength={60}
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Passkey name (e.g. iPhone, Work laptop)"
        aria-label="Passkey name"
        style={{ width: '100%', boxSizing: 'border-box', marginBottom: 'var(--spacing-sm)' }}
      />
      {!supported && <p className="tf-error" role="alert">This browser cannot create passkeys. Use a current Chrome, Safari or Edge on a device with a screen lock.</p>}
      {error && <p className="tf-error" role="alert">{error}</p>}
      <div className="tf-actions">
        <button type="submit" className="btn btn-primary" disabled={busy || !supported}>{busy ? 'Waiting for your device…' : 'Create passkey'}</button>
        {onCancel && <button type="button" className="btn btn-outline" onClick={onCancel} disabled={busy}>{cancelLabel}</button>}
      </div>
      <small className="tf-hint" style={{ marginTop: 'var(--spacing-sm)' }}>
        A passkey only works on this site and cannot be phished or copied. Add one on each device you manage users from (phone and computer) — or a synced passkey (iCloud Keychain, Google Password Manager) covers all of them.
      </small>
    </form>
  );
}
